import { useEffect, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { SignaturePad } from "@/components/common/SignaturePad";
import { ConfirmDialog } from "@/components/wizard/Controls";
import { PenLine, QrCode, Eraser, Download, Share2, Loader2, CheckCircle2, Copy } from "lucide-react";
import { toast } from "sonner";

function SignatureBox({ role, label, sig, consentId, onSaved, onQr, onClear }) {
  const pad = useRef(null);
  const [drawn, setDrawn] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setDrawn(false); }, [sig]);
  const save = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/consents/${consentId}/signature`, { role, data_url: pad.current.toDataURL() }); onSaved(data); toast.success(`${label} signature saved`); }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't save signature"); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-2xl border border-[#E2E8F0] p-4 dark:border-[#223139]" data-testid={`sig-box-${role}`}>
      <div className="mb-2 flex items-center justify-between"><span className="text-xs font-bold uppercase tracking-wider text-[#4E5F68] dark:text-[#8B9698]">{label} signature</span>
        {sig && <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#00A85A]"><CheckCircle2 className="h-3 w-3" />Signed {new Date(sig.signed_at).toLocaleString()}{sig.method === "qr" ? " · via phone" : ""}</span>}</div>
      {sig ? (
        <>
          <div className="flex h-40 items-center justify-center rounded-xl border border-[#E2E8F0] bg-white"><img src={sig.data_url} alt={`${label} signature`} className="max-h-36" data-testid={`sig-image-${role}`} /></div>
          <div className="mt-1 text-xs text-[#8B9698]">{sig.name}</div>
          <Button variant="outline" size="sm" className="mt-3" onClick={onClear} data-testid={`sig-clear-${role}`}><Eraser className="mr-1.5 h-3.5 w-3.5" />Clear & sign again</Button>
        </>
      ) : (
        <>
          <SignaturePad ref={pad} onChange={(empty) => setDrawn(!empty)} testid={`sig-pad-${role}`} />
          <div className="mt-3 flex flex-wrap gap-2">
            <Button size="sm" disabled={!drawn || busy} onClick={save} className="bg-[#00A85A] text-white hover:bg-[#008F4D]" data-testid={`sig-save-${role}`}>{busy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <PenLine className="mr-1.5 h-3.5 w-3.5" />}Save signature</Button>
            <Button size="sm" variant="outline" disabled={!drawn} onClick={() => { pad.current.clear(); setDrawn(false); }} data-testid={`sig-reset-${role}`}><Eraser className="mr-1.5 h-3.5 w-3.5" />Clear</Button>
            <Button size="sm" variant="outline" onClick={onQr} data-testid={`sig-qr-${role}`}><QrCode className="mr-1.5 h-3.5 w-3.5" />Sign on phone</Button>
          </div>
        </>
      )}
    </div>
  );
}

export function ConsentSignatures({ consent, onChange }) {
  const [qr, setQr] = useState(null);
  const [clearRole, setClearRole] = useState(null);
  const [share, setShare] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const sigs = consent.signatures || {};

  useEffect(() => {
    if (!qr) return undefined;
    const iv = setInterval(async () => {
      try { const { data } = await api.get(`/consents/${consent.id}`); if (data.signatures?.[qr.role]) { onChange(data); setQr(null); toast.success("Signature received from phone"); } } catch { /* keep polling */ }
    }, 2500);
    return () => clearInterval(iv);
  }, [qr, consent.id, onChange]);

  const openQr = async (role) => { try { const { data } = await api.post(`/consents/${consent.id}/sign-session`, { role }); setQr(data); } catch { toast.error("Couldn't create the signing link"); } };
  const clear = async () => { try { const { data } = await api.delete(`/consents/${consent.id}/signature/${clearRole}`); onChange(data); toast.success("Signature cleared"); } catch { toast.error("Couldn't clear signature"); } setClearRole(null); };
  const downloadPdf = async () => {
    setPdfBusy(true);
    try {
      const r = await api.get(`/consents/${consent.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = url; a.download = `CONTAHA_Consent_${(consent.patient_name || "patient").replace(/\s+/g, "_")}.pdf`; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Couldn't generate the PDF"); }
    finally { setPdfBusy(false); }
  };
  const doShare = async () => { try { const { data } = await api.post(`/consents/${consent.id}/share`); const link = `${window.location.origin}/c/${consent.token}`; setShare({ ...data, link }); } catch { toast.error("Couldn't create the share link"); } };
  const signLink = qr ? `${window.location.origin}/s/${qr.token}` : "";

  return (
    <div className="space-y-4" data-testid="consent-signatures">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <SignatureBox role="doctor" label="Doctor" sig={sigs.doctor} consentId={consent.id} onSaved={onChange} onQr={() => openQr("doctor")} onClear={() => setClearRole("doctor")} />
        <SignatureBox role="patient" label="Patient" sig={sigs.patient} consentId={consent.id} onSaved={onChange} onQr={() => openQr("patient")} onClear={() => setClearRole("patient")} />
      </div>
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" onClick={downloadPdf} disabled={pdfBusy} data-testid="consent-download-pdf">{pdfBusy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Download className="mr-1.5 h-4 w-4" />}Download PDF</Button>
        <Button variant="outline" onClick={doShare} data-testid="consent-share-link"><Share2 className="mr-1.5 h-4 w-4" />Share link</Button>
      </div>
      {share && (
        <div className="flex flex-wrap items-center gap-2 rounded-xl border border-[#00A85A]/30 bg-[#F4FBF7] px-3 py-2 text-xs dark:bg-[#00A85A]/10" data-testid="consent-share-panel">
          <span className="min-w-0 flex-1 truncate font-mono text-[#101516] dark:text-white">{share.link}</span>
          <span className="text-[#8B9698]">Expires {new Date(share.expires_at).toLocaleDateString()}</span>
          <button type="button" onClick={() => { navigator.clipboard?.writeText(share.link); toast.success("Link copied"); }} className="inline-flex items-center gap-1 font-semibold text-[#00A85A]" data-testid="consent-share-copy"><Copy className="h-3.5 w-3.5" />Copy</button>
        </div>
      )}
      <Dialog open={!!qr} onOpenChange={(o) => !o && setQr(null)}>
        <DialogContent className="max-w-sm" data-testid="sign-qr-modal">
          <DialogHeader><DialogTitle>Sign on phone — {qr?.role === "doctor" ? "Doctor" : "Patient"}</DialogTitle><DialogDescription>Scan with a phone camera. The signature appears here within seconds. Link valid for 15 minutes.</DialogDescription></DialogHeader>
          <div className="flex flex-col items-center gap-3">
            <div className="rounded-xl bg-white p-3"><QRCodeSVG value={signLink} size={180} /></div>
            <span className="inline-flex items-center gap-1.5 text-xs text-[#8B9698]"><Loader2 className="h-3 w-3 animate-spin" />Waiting for signature…</span>
            <button type="button" onClick={() => { navigator.clipboard?.writeText(signLink); toast.success("Signing link copied"); }} className="inline-flex items-center gap-1 text-xs font-semibold text-[#00A85A]" data-testid="sign-qr-copy"><Copy className="h-3.5 w-3.5" />Copy link</button>
          </div>
        </DialogContent>
      </Dialog>
      <ConfirmDialog open={!!clearRole} title="Clear this signature?" description="The saved signature will be removed so it can be signed again." confirmLabel="Clear" destructive onConfirm={clear} onCancel={() => setClearRole(null)} />
    </div>
  );
}
